
import { EMPLOYEE_ROLE, EMPLOYER_ROLE } from "@/constants/roles";
import {
  ARCHIVED_TASK_STATUS,
  COMPLETED_TASK_STATUS,
  EMPLOYEE_TABS,
  EMPLOYEE_TASKS_TABS,
  EMPLOYEES_TASKS_TAB,
  EMPLOYER_TABS,
  EMPLOYER_TASKS_TABS,
  MY_TASKS_TAB,
} from "@/constants/tasks";
import { TNewTask_Table, TTask_Table } from "@/models/tasks";
import { RCandidateServices } from "@/modules/Services/RServices";
import { SMTasks, SMUser } from "@/store";
import { computed, defineComponent, getCurrentInstance, onMounted, ref } from "vue-demi";

const CASE_PRODUCT_TYPE = "case";

const VALUE_NAME = "name";
const VALUE_STATUS = "status";
const VALUE_SECTION = "complianceSection";
const VALUE_ACTION_BY = "actionBy";
const VALUE_DUE_DATE = "dueDate";

export default defineComponent({
  name: "TasksTableV3",

  setup() {
    const instance = getCurrentInstance()?.proxy;
    const route = instance?.$route;
    const router = instance?.$router;

    const loading = ref(false);

    const excludeStatuses = [COMPLETED_TASK_STATUS, ARCHIVED_TASK_STATUS];

    const companyId = computed(() => {
      return Number(route?.params?.company_id ?? 0);
    });

    const tabName = ref("");

    const tabValues = computed(() => {
      return SMUser.isEmployer ? EMPLOYER_TABS : EMPLOYEE_TABS;
    });

    const tabNames = computed(() => {
      return SMUser.isEmployer ? EMPLOYER_TASKS_TABS : EMPLOYEE_TASKS_TABS;
    });

    const mainTasks = computed(() => {
      return SMTasks.myTasks;
    });

    const sideTasks = computed(() => {
      return SMTasks.sideTasks;
    });

    const isEmployee = computed(() => SMUser.isEmployee);

    const isComplianceBlocked = computed(() => {
      return SMUser.user?.is_compliance_blocked ?? false;
    });

    const isEmployeeComplianceBlocked = computed(() => {
      return isComplianceBlocked.value && isEmployee.value;
    });

    async function getTableTasks() {
      loading.value = true;

      const role = SMUser.isEmployer ? EMPLOYER_ROLE : EMPLOYEE_ROLE;
      const sideRole = SMUser.isEmployer ? EMPLOYEE_ROLE : EMPLOYER_ROLE;

      await SMTasks.getMyTasks({ role, excludeStatuses });
      await SMTasks.getSideTasks({ role: sideRole, excludeStatuses });

      loading.value = false;
    }

    function navigateTask(item: TNewTask_Table | TTask_Table) {
      const { id } = item;

      let path = `${SMUser.basePath}/tasks/${id}`;

      if ((item as TTask_Table)?.productType === CASE_PRODUCT_TYPE) {
        path = `/${RCandidateServices.path}/tasks/${id}`;
      }

      if (companyId.value) {
        path = `/expert/companies/${companyId.value}/tasks/${id}`;
      }

      router?.push(path);
    }

    const tableHeaders = computed(() => {
      return [
        {
          label: "Task name",
          value: VALUE_NAME,
          span: 3,
          action: navigateTask,
        },
        {
          label: "Status",
          value: VALUE_STATUS,
          filter: true,
          span: 0.5,
        },
        {
          label: "Compliance section",
          value: VALUE_SECTION,
        },
        {
          label: "Due date",
          value: VALUE_DUE_DATE,
          info: "Complete the task by that date to be compliant",
          infoPosition: "left",
          span: 0.5,
        },
      ];
    });

    const tableWithActionByColumnHeaders = computed(() => {
      return [
        {
          label: "Task name",
          value: VALUE_NAME,
          span: 2,
          action: navigateTask,
        },
        {
          label: "Status",
          value: VALUE_STATUS,
          filter: true,
          span: 0.5,
        },
        {
          label: "Compliance section",
          value: VALUE_SECTION,
        },
        {
          label: "Action by",
          value: VALUE_ACTION_BY,
          filter: true,
          filterSearch: true,
          span: 0.5,
        },
        {
          label: "Due date",
          value: VALUE_DUE_DATE,
          info: "Complete the task by that date to be compliant",
          infoPosition: "left",
          span: 0.5,
        },
      ];
    });

    const headers = computed(() => {
      const raw =
        tabName.value === MY_TASKS_TAB || !SMUser.isEmployer
          ? tableHeaders.value
          : tableWithActionByColumnHeaders.value;

      if (!isEmployeeComplianceBlocked.value) return raw;

      return raw.filter((header) => header.value !== VALUE_STATUS);
    });

    function getTabCount(tabValue: string) {
      if (tabValue === MY_TASKS_TAB) {
        return mainTasks.value.length;
      } else if (tabValue === EMPLOYEES_TASKS_TAB) {
        return sideTasks.value.length;
      }
      return 0;
    }

    const tabs = computed(() => {
      return tabValues.value.map((value) => ({
        title: tabNames.value[value] || value,
        value,
        count: getTabCount(value),
      }));
    });

    const dataForTabs = computed(() => {
      const result: { [key: string]: (TNewTask_Table & { inactive?: boolean })[] } = {};
      tabValues.value.forEach((tabValue) => {
        let tasks: TNewTask_Table[] = [];
        if (tabValue === MY_TASKS_TAB) {
          tasks = mainTasks.value;
        } else if (tabValue === EMPLOYEES_TASKS_TAB) {
          tasks = sideTasks.value;
        }

        result[tabValue] = isEmployeeComplianceBlocked.value
          ? tasks.map((task) => ({ ...task, inactive: true }))
          : tasks;
      });
      return result;
    });

    function onTabSelect(tab: string) {
      tabName.value = tab;
    }

    onMounted(async () => {
      if (instance?.$root) {
        instance.$root.$on("update-company-level-data", getTableTasks);
      }

      if (companyId.value) {
        SMTasks.setCompanyId(companyId.value);
      }

      await getTableTasks();
    });

    return {
      loading,
      headers,
      tabs,
      dataForTabs,
      isEmployeeComplianceBlocked,
      navigateTask,
      onTabSelect,
    };
  },
});
